import React from "react";
import {
  Box,
  Card,
  Chip,
  IconButton,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import {
  DeleteOutlined,
  ErrorOutlineOutlined,
  HourglassEmptyOutlined,
  PlayCircleOutlineOutlined,
  SmartToyOutlined,
  CheckCircleOutlined,
} from "@mui/icons-material";

export default function VideoAgentJobCard({ item, onClick, onDelete, lang }) {
  const theme = useTheme();
  const [hover, setHover] = React.useState(false);

  const st = String(item?.status || "").toLowerCase();
  const isProcessing = st === "processing" || st === "pending";
  const isFailed = st === "failed" || st === "error";
  const isDone = st === "completed" && item?.generated_video;

  const title = String(item?.title || item?.prompt || "").trim() || lang?.videoAgentUntitled || "Video";
  const previewLine = String(item?.prompt || "").trim().slice(0, 120);

  return (
    <Card
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={onClick}
      sx={{
        position: "relative",
        borderRadius: 2,
        overflow: "hidden",
        cursor: "pointer",
        transition: "transform 0.2s, border-color 0.2s",
        border: `1px solid ${theme.palette.divider}`,
        height: 280,
        "&:hover": {
          transform: "translateY(-4px)",
          borderColor: alpha(theme.palette.primary.main, 0.55),
        },
      }}
    >
      {isProcessing && (
        <Box
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: alpha(theme.palette.background.default, 0.95),
            px: 2,
          }}
        >
          <HourglassEmptyOutlined color="warning" sx={{ fontSize: 44, mb: 1.5 }} />
          <Typography variant="subtitle2" fontWeight={800} color="warning.main" textAlign="center">
            {lang?.statusProcessing || "Processing"}
          </Typography>
          <Typography variant="caption" color="text.secondary" textAlign="center" sx={{ mt: 0.5 }}>
            {item?.heygen_status || ""}
          </Typography>
        </Box>
      )}

      {isFailed && (
        <Box
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: alpha(theme.palette.error.main, 0.06),
            px: 2,
          }}
        >
          <ErrorOutlineOutlined color="error" sx={{ fontSize: 44, mb: 1.5 }} />
          <Typography variant="subtitle2" fontWeight={800} color="error" textAlign="center">
            {lang?.error || "Failed"}
          </Typography>
          <Typography variant="caption" color="text.secondary" textAlign="center" noWrap sx={{ maxWidth: "100%" }}>
            {String(item?.error_message || "").slice(0, 140)}
          </Typography>
        </Box>
      )}

      {isDone && (
        <Box sx={{ position: "relative", height: "100%" }}>
          <Box
            sx={{
              height: 168,
              bgcolor: alpha(theme.palette.primary.main, 0.12),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <PlayCircleOutlineOutlined sx={{ fontSize: 64, color: "primary.main", opacity: hover ? 1 : 0.85 }} />
          </Box>
          <Box sx={{ p: 1.5 }}>
            <StackRow title={title} line={previewLine} />
            <Chip
              size="small"
              icon={<CheckCircleOutlined />}
              label={lang?.videoAgentReadyChip || "Saved"}
              color="success"
              variant="outlined"
              sx={{ mt: 1, fontWeight: 700 }}
            />
          </Box>
        </Box>
      )}

      {!isProcessing && !isFailed && !isDone && (
        <Box sx={{ p: 2, height: "100%", display: "flex", flexDirection: "column" }}>
          <SmartToyOutlined sx={{ fontSize: 40, color: "text.secondary", mb: 1 }} />
          <Typography fontWeight={800} noWrap>
            {title}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ flex: 1, mt: 0.5 }}>
            {previewLine}
          </Typography>
          <Chip size="small" label={st || "—"} sx={{ alignSelf: "flex-start", mt: 1 }} />
        </Box>
      )}

      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          onDelete?.(item);
        }}
        sx={{
          position: "absolute",
          top: 8,
          right: 8,
          bgcolor: alpha("#000", 0.45),
          color: "common.white",
          "&:hover": { bgcolor: alpha("#000", 0.65) },
        }}
      >
        <DeleteOutlined fontSize="small" />
      </IconButton>
    </Card>
  );
}

function StackRow({ title, line }) {
  return (
    <>
      <Typography fontWeight={800} noWrap>
        {title}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }} noWrap>
        {line}
      </Typography>
    </>
  );
}
